/**
 * @module color
 * @description Provides utilities for color conversion and gradient generation for various plot types.
 */
import { round, isEmpty, toString } from "lodash";

/**
 * Interface for plot properties used by gradient generators
 */
export interface PlotProps {
  maxLevel?: number;
  minLevel?: number;
  target?: number;
  accuracy?: number;
  stepSize?: number;
  labels?: Record<string, number>;
  isActive?: boolean;  // For dual-imager camera active/inactive state
}

/**
 * Type for RGBA color array [r, g, b, a]
 */
type RGBAColor = [number, number, number, number];

/**
 * Type for gradient map (mapping numeric indices to RGBA colors)
 */
type GradientMap = Record<number, RGBAColor>;

/**
 * Converts HSL color values to RGB.
 * @function
 * @param {number} h - The hue value (0 to 1).
 * @param {number} [s=1] - The saturation value (0 to 1).
 * @param {number} [l=0.5] - The lightness value (0 to 1).
 * @param {number} [a=1] - The alpha (opacity) value (0 to 1).
 * @returns {RGBAColor} An array of [r, g, b, a] values (RGB 0 to 255, alpha 0 to 1).
 */
const hslToRgba = (h: number, s = 1, l = 0.5, a = 1): RGBAColor => {
  const hue2rgb = (p: number, q: number, t: number): number => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const r = hue2rgb(p, q, h + 1 / 3);
  const g = hue2rgb(p, q, h);
  const b = hue2rgb(p, q, h - 1 / 3);
  return [round(r * 255), round(g * 255), round(b * 255), a]; 
};

/**
 * @function getLabels
 * @description Generates labels for the scale based on plot properties.
 * @param {PlotProps} plotProps - The plot properties
 * @returns {Record<string, number>} An object with label values as keys and corresponding plot values
 */
export const getLabels = (plotProps: PlotProps): Record<string, number> => {
  const { 
    labels = {}, 
    minLevel = 0, 
    maxLevel = 100, 
    stepSize = (maxLevel - minLevel) / 6 
  } = plotProps;
  
  const result = { ...labels };
  
  if (isEmpty(result)) {
    for (let i = minLevel + stepSize; i < maxLevel; i += stepSize) {
      result[toString(i)] = i;
    }
  }
  
  return result;
};

/**
 * @function getSplTickValues
 * @description Computes evenly spaced whole-dBA tick values across an SPL
 * legend range, used to label the Direct/Total SPL scales. The step is rounded
 * to a whole number (min 1 dB) so labels stay clean, aiming for ~5 ticks.
 * Returns an empty array for a non-finite or non-positive range. (PIG-215)
 * @param {number} minLevel - The minimum SPL value of the range
 * @param {number} maxLevel - The maximum SPL value of the range
 * @returns {number[]} The tick values to label
 */
export const getSplTickValues = (minLevel: number, maxLevel: number): number[] => {
  if (!isFinite(minLevel) || !isFinite(maxLevel) || maxLevel <= minLevel) {
    return [];
  }
  const step = Math.max(1, Math.round((maxLevel - minLevel) / 5));
  const ticks: number[] = [];
  for (let value = Math.ceil(minLevel); value <= maxLevel; value += step) {
    ticks.push(value);
  }
  return ticks;
};

/**
 * @function range2Value
 * @description Converts a pixel position to a value in the plot range.
 * @param {PlotProps} plotProps - The plot properties
 * @param {number} canvasWidth - The width of the canvas
 * @param {number} x - The x-coordinate on the canvas
 * @returns {number} The corresponding value in the plot range
 */
export const range2Value = (plotProps: PlotProps, canvasWidth: number, x: number): number => {
  const { minLevel = 0, maxLevel = 100, accuracy = 1 } = plotProps;
  return round(((maxLevel - minLevel) / canvasWidth * x + minLevel) * accuracy) / accuracy;
};

/**
 * @function value2Range
 * @description Converts a value in the plot range to a pixel position on the canvas.
 * @param {PlotProps} plotProps - The plot properties
 * @param {number} canvasWidth - The width of the canvas
 * @param {number} level - The value in the plot range
 * @returns {number} The corresponding x-coordinate on the canvas
 */
export const value2Range = (plotProps: PlotProps, canvasWidth: number, level: number): number => {
  const { minLevel = 0, maxLevel = 100 } = plotProps;
  // inRange(pixelLevel, minLevel) ? minLevel : !inRange(pixelLevel, maxLevel) ? maxLevel : pixelLevel
  return ((level - minLevel) * (canvasWidth / (maxLevel - minLevel)));
};

/**
 * Type for gradient generator functions
 */
type GradientGenerator = (plotProps: PlotProps) => GradientMap;

/**
 * Object containing gradient generation functions for different plot types.
 */
export const gradients: Record<string, GradientGenerator> = {
  /**
   * Generates color gradients for Sound Pressure Level (SPL) plots.
   * @function
   * @param {PlotProps} plotProps - The plot properties.
   * @param {number} [plotProps.maxLevel=110] - The maximum SPL value.
   * @param {number} [plotProps.minLevel=60] - The minimum SPL value.
   * @param {number} [plotProps.target=90] - The target SPL value.
   * @param {number} [plotProps.accuracy=10] - The accuracy of the gradient.
   * @returns {GradientMap} The gradient map of index to RGBA color.
   */
  SPL: (plotProps: PlotProps): GradientMap => {
    const { minLevel = 60, accuracy = 10 } = plotProps;
    let { maxLevel = 110, target = 90 } = plotProps;
    // Guard against a degenerate range: a flat SPL matrix (min === max) or a
    // target sitting on an endpoint would make the hue slopes divide by zero and
    // emit NaN colours. Keep a minimum 1 dB span with the target strictly
    // inside it. (PIG-219)
    if (!(maxLevel > minLevel)) {
      maxLevel = minLevel + 1;
    }
    target = Math.min(Math.max(target, minLevel + 0.001), maxLevel - 0.001);
    const gradientRGBA: GradientMap = {};
    const hue_start = 250; // 180 cyan change to 250 for blue
    const hue_yellow = 60;
    const hue_end = 0; // red
    const m1 = (hue_yellow - hue_start) / (target - minLevel);
    const b1 = 60 - m1 * target;
    const m2 = (hue_end - hue_yellow) / (maxLevel - target);
    const b2 = 60 - m2 * target;
    
    for (let i = minLevel; i <= maxLevel; i += 1 / accuracy) {
      const index = round(i * accuracy); // index is decibel*10
      const hue = i <= target ? i * m1 + b1 : i * m2 + b2;
      gradientRGBA[index] = hslToRgba(hue / 360);
    }
    
    return gradientRGBA;
  },

  /**
   * Generates color gradients for Speech Transmission Index (STI) plots.
   * @function
   * @param {PlotProps} plotProps - The plot properties.
   * @param {number} [plotProps.maxLevel=1] - The maximum STI value.
   * @param {number} [plotProps.minLevel=0] - The minimum STI value.
   * @param {number} [plotProps.accuracy=1000] - The accuracy of the gradient.
   * @returns {GradientMap} The gradient map of index to RGBA color.
   */  
  STI: (plotProps: PlotProps): GradientMap => {
    const { maxLevel = 1, minLevel = 0, accuracy = 1000 } = plotProps;
    const gradientRGBA: GradientMap = {};
    const hue_end = 120; // stop at green
    const shift = 0.2; // move yellow to the left
    
    for (let i = minLevel; i <= maxLevel; i += 1 / accuracy) {
      const index = round(i * accuracy);
      const hue = Math.min(hue_end, index * (hue_end / accuracy) / (maxLevel - shift));
      gradientRGBA[index] = hslToRgba(hue / 360);
    }
    
    return gradientRGBA;
  },

  /**
   * Generates color gradients for microphone distance plots.
   * @function
   * @param {PlotProps} plotProps - The plot properties.
   * @param {number} [plotProps.maxLevel=6] - The maximum distance value.
   * @param {number} [plotProps.minLevel=0] - The minimum distance value.
   * @param {number} [plotProps.target=2.4755] - The target distance value.
   * @param {number} [plotProps.accuracy=100] - The accuracy of the gradient.
   * @returns {GradientMap} The gradient map of index to RGBA color.
   */  
  micDistance: (plotProps: PlotProps): GradientMap => {
    const { maxLevel = 6, minLevel = 0, target = 2.4755, accuracy = 100 } = plotProps;
    const hue = 124;
    const fade = 0.5; // mic fade distance
    const gradientRGBA: GradientMap = {};
    let opacity = 0.8;
    
    for (let i = minLevel; i <= maxLevel * accuracy; i++) {
      const dist = i / accuracy;
      if (dist > target) {
        const slope = (1 / fade) / target; // fade slope
        opacity = Math.max(0, ((target - dist) * slope) + 0.8);
      }
      gradientRGBA[i] = hslToRgba(hue / 360, undefined, undefined, opacity);
    }
    
    return gradientRGBA;
  },

  /**
   * Generates color gradients for relative average level plots.
   * @function
   * @param {PlotProps} plotProps - The plot properties.
   * @param {number} [plotProps.maxLevel=6] - The maximum level value.
   * @param {number} [plotProps.minLevel=-12] - The minimum level value.
   * @param {number} [plotProps.accuracy=100] - The accuracy of the gradient.
   * @returns {GradientMap} The gradient map of index to RGBA color.
   */    
  relativeAverage: (plotProps: PlotProps): GradientMap => {
    const { maxLevel = 6, minLevel = -12, accuracy = 100 } = plotProps;
    const gradientRGBA: GradientMap = {};
    
    for (let avg = minLevel; avg <= maxLevel; avg += 1/accuracy) {
      const index = Math.round(avg * accuracy);
      let hue = 0;
      
      switch (true) {
        case avg <= -2:
          hue = Math.max(120, Math.min(-26 * avg + 66, 240));
          break;
        case avg <= 2:
          hue = (-61 * (avg + 4.5)) / 6 + 131;
          break;
        case avg <= 5:
          hue = Math.min(Math.max(0, 462 - 194 * avg + 20.4 * avg * avg), 65);
          break;
        default:
          hue = 0;
      }
      
      gradientRGBA[index] = hslToRgba(hue / 360);
    }
    
    return gradientRGBA;
  }, 

  /**
   * Generates color gradients for camera distance plots matching Figma design.
   * Uses #418EFF blue for active plots or #0048B2 for inactive plots, with gradient to white/dark background.
   * Fade starts at coverageRadius and ends at limitRadius.
   * @function
   * @param {PlotProps} plotProps - The plot properties.
   * @param {number} [plotProps.maxLevel=9] - The maximum distance value (normalized scale).
   * @param {number} [plotProps.minLevel=0] - The minimum distance value.
   * @param {number} [plotProps.target=5] - The coverage radius (where fade starts).
   * @param {number} [plotProps.accuracy=100] - The accuracy of the gradient.
   * @param {boolean} [plotProps.isActive=true] - Whether this is the active plot (affects base color).
   * @returns {GradientMap} The gradient map of index to RGBA color.
   */
  cameraCoverage: (plotProps: PlotProps & { themeMode?: string; coverageRadius?: number; limitRadius?: number; isActive?: boolean }): GradientMap => {
    const { maxLevel = 9, minLevel = 0, target = 5, accuracy = 100, themeMode = 'light', coverageRadius, limitRadius, isActive = true } = plotProps;
    const gradientRGBA: GradientMap = {};

    // Select base color based on active state
    // Active: #418EFF blue (Figma design)
    // Inactive: #0048B2 darker blue
    const baseColor = isActive
      ? { r: 65, g: 142, b: 255 }   // Active: #418EFF
      : { r: 0, g: 72, b: 178 };     // Inactive: #0048B2
    const whiteColor = { r: 255, g: 255, b: 255 }; // #FFF for light mode
    const darkBgColor = { r: 34, g: 34, b: 34 }; // #222222 dark background
    const baseOpacity = 0.75;

    // In dark mode, fade to dark background color instead of white
    const isDarkMode = themeMode === 'dark';
    const targetColor = isDarkMode ? darkBgColor : whiteColor;

    // Calculate fade threshold based on coverageRadius/limitRadius ratio
    // If not provided, use target/maxLevel ratio (backwards compatible)
    const fadeThreshold = (coverageRadius && limitRadius)
      ? coverageRadius / limitRadius
      : target / maxLevel;

    for (let i = minLevel; i <= maxLevel * accuracy; i++) {
      const dist = i / accuracy;
      const normalizedDist = dist / maxLevel; // Normalize to 0-1 range

      let r: number, g: number, b: number, opacity: number;

      if (normalizedDist <= fadeThreshold) {
        // Pure blue region (0 to coverageRadius) - same for both themes
        r = baseColor.r;
        g = baseColor.g;
        b = baseColor.b;
        opacity = baseOpacity;
      } else {
        // Gradient transition region (coverageRadius to limitRadius) - fade to target color
        const transitionProgress = (normalizedDist - fadeThreshold) / (1 - fadeThreshold);
        r = Math.round(baseColor.r + (targetColor.r - baseColor.r) * transitionProgress);
        g = Math.round(baseColor.g + (targetColor.g - baseColor.g) * transitionProgress);
        b = Math.round(baseColor.b + (targetColor.b - baseColor.b) * transitionProgress);

        if (isDarkMode) {
          // Dark mode: Fade opacity more aggressively to blend with dark background
          opacity = baseOpacity * (1 - transitionProgress * 0.8);
        } else {
          // Light mode: Maintain better opacity throughout the transition
          opacity = baseOpacity * (1 - transitionProgress * 0.4);
        }
      }

      gradientRGBA[i] = [r, g, b, opacity];
    }

    return gradientRGBA;
  },
};
